import { useEffect, useState } from 'preact/hooks';
import { useRevalidate } from '../state/useRevalidate';
import type { Invalidation } from '../state/events';
import { request, type Session, describeError } from '../api/client';
import { logUnit, tracker, undoUnit } from '../api/habits';
import {
  dayLabel,
  dayNumber,
  isoWeek,
  shiftWeek,
  weekLabel,
  weekOf,
  type WeekStart,
  type Week as WeekRange,
} from '../api/week';
import { glyphStateFor } from '../api/adapter';
import type { WireEntry, WireTracker } from '../api/wire';

import { SectionHeading } from '../components/composed/SectionHeading';
import { HabitCell } from '../components/trackers/HabitCell.jsx';
import { EmptyState } from '../components/feedback/EmptyState.jsx';
import { GlyphMark } from '../components/entries/GlyphMark.jsx';
import { Pressable } from '../components/a11y/Pressable';


/** Habits and dated tasks for one Monday–Sunday week.
 *
 *  Seven day columns fit at 390px, which the reference frame proves. Two
 *  deviations from that frame, both deliberate:
 *
 *  1. **The `5/7` hit column is restored.** §06 claimed it fits and the frame
 *     omitted it; the TUI has it and it is the one number that makes the grid
 *     worth scanning. It is counted here, over exactly the seven days on screen —
 *     never from the service's `weekly_count`, which is a rolling seven days
 *     ending today and would quietly mean something else.
 *  2. **D-2 follows the design after all: glyph, text, date chip.** An earlier
 *     pass put a seven-cell mark row under each task to preserve an
 *     across-the-week scan. That was an invention — the design frame shows a
 *     flat row with the day as a chip on the right, which is both more legible
 *     and easier to hit. The scan it gives up was never worth the two lines.
 */
export function Week({
  session,
  journalDate,
  onOpenActions,
  invalidation,
  refresh,
  weekStart = 'monday',
}: {
  session: Session;
  journalDate: string;
  onOpenActions: (entry: WireEntry) => void;
  /** The latest recorded change. Reloads only when it falls in the week on
   *  screen, which is not necessarily the week the journal calls today. */
  invalidation?: Invalidation | null;
  /** Bumped when something on this device changed the journal. A signal, not a
   *  key — see the note in `Today`. */
  refresh?: number;
  /** Monday or Sunday, from Settings. Ranges stay inclusive either way. */
  weekStart?: WeekStart;
}) {
  const [range, setRange] = useState<WeekRange>(() => weekOf(journalDate, weekStart));
  // Changing the preference re-frames the week around the same day rather than
  // jumping back to today's week.
  useEffect(() => {
    setRange((current) => weekOf(current.days[3] ?? journalDate, weekStart));
  }, [weekStart, journalDate]);
  const revalidate = useRevalidate(invalidation ?? null, {
    kind: 'week',
    from: range.from,
    to: range.to,
  });
  const [habits, setHabits] = useState<WireTracker | null>(null);
  const [tasks, setTasks] = useState<WireEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function load(target: WeekRange) {
    setError(null);
    try {
      const [t, entries] = await Promise.all([
        tracker(session, target.from, target.to).catch(() => null),
        request<{ items: WireEntry[] }>(
          session,
          // Inclusive at both ends. Month-scoped tasks are intentionally absent
          // from date-range queries and belong to the Monthly Log.
          `/v1/entries?kind=task&from=${target.from}&to=${target.to}&limit=100`,
        ).then((r) => r.body.items),
      ]);
      setHabits(t);
      setTasks(entries);
    } catch (caught) {
      setError(describeError(caught));
    }
  }

  useEffect(() => {
    void load(range);
  }, [range, session, revalidate, refresh]);

  function countFor(habitId: string, date: string): number {
    const habit = habits?.items.find((h) => h.id === habitId);
    return habit?.days.find((d) => d.journal_date === date)?.count ?? 0;
  }

  /** Tap adds a unit; a second tap on a met goal removes one, so a cell is a
   *  toggle for the common case of a goal of 1 without hiding the count. */
  async function tapCell(habitId: string, date: string, goal: number) {
    const cell = `${habitId}:${date}`;
    if (busy) return;
    setBusy(cell);
    const current = countFor(habitId, date);
    try {
      if (current >= goal && goal > 0) {
        await undoUnit(session, habitId, date, current);
      } else {
        await logUnit(session, habitId, date);
      }
      // Refetch rather than patch: the marks and the hit column can then never
      // disagree with the service, which is the rule the TUI follows too.
      await load(range);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setBusy(null);
    }
  }

  const items = habits?.items ?? [];

  return (
    <main style={{ padding: 'var(--bujo-gutter)', maxWidth: 560, margin: '0 auto' }}>
      <header
        style={{
          paddingBottom: 'var(--bujo-space-2)',
          marginBottom: 'var(--bujo-space-4)',
          borderBottom: '1px solid var(--bujo-rule-heavy)',
        }}
      >
        <div style={eyebrow}>BUJO / WEEKLY LOG</div>
        <div style={{ display: 'flex', gap: 'var(--bujo-space-3)', alignItems: 'baseline', flexWrap: 'wrap' }}>
          <h1 style={{ margin: 0, font: 'var(--bujo-fw-medium) var(--bujo-fs-display)/1.1 var(--bujo-font-mono)', color: 'var(--bujo-accent)' }}>
            WEEK {isoWeek(range.from)}
          </h1>
          {/* The range is metadata beside the week number, not the headline. */}
          <span style={{ font: 'var(--bujo-fw-regular) var(--bujo-fs-event)/1.2 var(--bujo-font-mono)', color: 'var(--bujo-text-meta)' }}>
            {weekLabel(range)}
          </span>
        </div>
        <nav
          aria-label="Change week"
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            gap: 'var(--bujo-space-3)',
            paddingTop: 'var(--bujo-space-1)',
          }}
        >
          <button type="button" onClick={() => setRange(shiftWeek(range, -1))} style={nav}>
            ◀ W{isoWeek(shiftWeek(range, -1).from)}
          </button>
          {/* States the two conventions that are easy to get wrong. */}
          <span style={{ ...nav, cursor: 'default', letterSpacing: 'var(--bujo-ls-badge)' }}>
            MON-START · INCLUSIVE
          </span>
          <button type="button" onClick={() => setRange(shiftWeek(range, 1))} style={nav}>
            W{isoWeek(shiftWeek(range, 1).from)} ▶
          </button>
        </nav>
      </header>

      {error ? (
        <p style={{ font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1.4 var(--bujo-font-mono)', color: 'var(--bujo-state-error)' }}>
          ! {error}
        </p>
      ) : null}

      <section style={{ paddingBottom: 'var(--bujo-space-5)' }}>
        <SectionHeading active note="TAP CELL · LOG UNIT">
          HABITS
        </SectionHeading>
        <div style={gridScroller}>
        <Header days={range.days} today={journalDate} />
        {items.length === 0 ? (
          <EmptyState line="No habits yet." detail="Add one from the habits page." />
        ) : (
          items.map((habit) => {
            const hit = range.days.filter((d) => {
              const c = countFor(habit.id, d);
              return c > 0 && c >= (habit.goal_per_day || 1);
            }).length;
            return (
              <div key={habit.id} style={row}>
                <span style={labelCell}>{habit.name}</span>
                <span style={hitCell}>{hit}/7</span>
                {range.days.map((date) => (
                  <Pressable
                    key={date}
                    label={`${habit.name}, ${dayLabel(date)} ${dayNumber(date)}: ${countFor(habit.id, date)} of ${habit.goal_per_day || 1}`}
                    onPress={() => void tapCell(habit.id, date, habit.goal_per_day || 1)}
                    style={cellSlot}
                  >
                    <HabitCell
                      value={countFor(habit.id, date)}
                      goal={habit.goal_per_day || 1}
                      counted={(habit.goal_per_day || 1) > 1}
                      today={date === journalDate}
                    />
                  </Pressable>
                ))}
              </div>
            );
          })
        )}
        </div>
        {items.length > 0 ? (
          <div style={{ paddingTop: 'var(--bujo-space-2)' }}>
            <div style={legend}>tap a cell to log a unit · tap a met goal to undo one</div>
            <div style={legend}>
              <b style={{ color: 'var(--bujo-state-done)' }}>x</b> goal met&nbsp;&nbsp;
              <b>n</b> partial count&nbsp;&nbsp;
              <b>·</b> none
            </div>
          </div>
        ) : null}
      </section>

      <section>
        <SectionHeading muted note={tasks && tasks.length > 6 ? `${tasks.length} · SCROLL FOR ALL` : `${tasks?.length ?? 0} DATED`}>
          TASKS
        </SectionHeading>
        {tasks === null ? null : tasks.length === 0 ? (
          <EmptyState line="No dated tasks this week." />
        ) : (
          tasks.map((task) => (
            <Pressable
              key={task.id}
              label={`${task.content} — ${task.active_date ? dayLabel(task.active_date) + ' ' + dayNumber(task.active_date) : 'undated'} — open actions`}
              onPress={() => onOpenActions(task)}
            >
              <div style={taskRow}>
                <GlyphMark state={glyphStateFor(task)} priority={task.priority} />
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    font: 'var(--bujo-fw-regular) var(--bujo-fs-event)/var(--bujo-lh-row) var(--bujo-font-mono)',
                    color: task.state === 'open' ? 'var(--bujo-text-primary)' : 'var(--bujo-text-dim)',
                    textDecoration: task.state === 'cancelled' ? 'line-through' : 'none',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {task.content}
                </span>
                {task.active_date ? <DayChip date={task.active_date} today={journalDate} /> : null}
              </div>
            </Pressable>
          ))
        )}
      </section>

    </main>
  );
}

/** The day a task sits on, as a chip. Amber outline when it is today, so the
 *  current day reads at a glance without colour being the only signal — the
 *  weekday and number are in the chip either way. */
function DayChip({ date, today }: { date: string; today: string }) {
  const isToday = date === today;
  return (
    <span
      style={{
        flex: 'none',
        padding: '3px 6px',
        border: `1px solid ${isToday ? 'var(--bujo-accent)' : 'var(--bujo-rule-hairline)'}`,
        color: isToday ? 'var(--bujo-accent)' : 'var(--bujo-text-meta)',
        font: 'var(--bujo-fw-regular) var(--bujo-fs-badge)/1 var(--bujo-font-mono)',
        letterSpacing: 'var(--bujo-ls-badge)',
      }}
    >
      {dayLabel(date)} {dayNumber(date)}
    </span>
  );
}

function Header({ days, today }: { days: string[]; today: string }) {
  return (
    <div style={row}>
      <span style={labelCell} />
      <span style={{ ...hitCell, color: 'var(--bujo-text-meta)' }}>HIT</span>
      {days.map((date) => (
        <span
          key={date}
          style={{
            ...cellSlot,
            textAlign: 'center',
            font: `${date === today ? 'var(--bujo-fw-bold)' : 'var(--bujo-fw-regular)'} var(--bujo-fs-badge)/1.3 var(--bujo-font-mono)`,
            color: date === today ? 'var(--bujo-accent)' : 'var(--bujo-text-meta)',
          }}
        >
          {dayNumber(date)}
          <br />
          {dayLabel(date)}
        </span>
      ))}
    </div>
  );
}

const row = {
  display: 'flex',
  gap: 2,
  alignItems: 'center',
  minHeight: 'var(--bujo-row-min)',
  borderBottom: '1px solid var(--bujo-rule-hairline)',
  // 76 label + 30 hit + 7 × 32 floor + 8 × 2 gaps. Below this the row scrolls
  // inside `gridScroller`; above it the cells flex and nothing scrolls.
  minWidth: 346,
};

/** Cells are a fixed width so the grid keeps its shape instead of stretching to
 *  whatever the viewport is. Seven of these plus the label and hit columns is
 *  what fits 390px; on a wider screen the grid simply stops rather than
 *  smearing, which is what the reference frame does. */
/** Cells share whatever the row has left, down to a floor.
 *
 *  They were a fixed 38px, with a comment claiming nine columns fit 390px. They
 *  did not: 76 + 34 + 7×38 + gaps is 392px inside a 350px content box, so the
 *  *page* scrolled sideways at every phone width — the document measured 412px
 *  wide in both a 320 and a 390 viewport. A page that scrolls horizontally is
 *  the one layout failure with no acceptable version.
 *
 *  Flexing to fit also answers the note that the boxes looked too wide: at 390
 *  they land near 35px instead of 38. Below the floor the grid scrolls inside
 *  its own container instead of taking the page with it. */
const cellSlot = {
  flex: '1 1 0' as const,
  minWidth: 32,
};

const labelCell = {
  width: 76,
  flex: 'none' as const,
  paddingRight: 'var(--bujo-space-2)',
  // Wraps rather than clipping to five characters: `EAT <= 7PM` is legible over
  // two lines and meaningless truncated.
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1.25 var(--bujo-font-mono)',
  color: 'var(--bujo-text-primary)',
  // Stays put while the day columns scroll under it, so a scrolled grid never
  // becomes seven anonymous numbers.
  position: 'sticky' as const,
  left: 0,
  background: 'var(--bujo-surface-page)',
  zIndex: 1,
};

const hitCell = {
  width: 30,
  flex: 'none' as const,
  textAlign: 'right' as const,
  paddingRight: 'var(--bujo-space-2)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
  position: 'sticky' as const,
  left: 76,
  background: 'var(--bujo-surface-page)',
  zIndex: 1,
};

/** The grid's own scroll context. At 320 the seven columns cannot fit beside the
 *  habit name at a legible size, so the grid scrolls here rather than the page. */
const gridScroller = {
  overflowX: 'auto' as const,
  overscrollBehaviorX: 'contain' as const,
};

const taskRow = {
  display: 'flex',
  gap: 'var(--bujo-space-2)',
  alignItems: 'center',
  minHeight: 'var(--bujo-row-min)',
  borderBottom: '1px solid var(--bujo-rule-hairline)',
};

/** The glyph legend. State is a glyph first and a word here, so the grid stays
 *  readable without colour. */
const legend = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-micro)/1.6 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
};

const eyebrow = {
  font: 'var(--bujo-fw-regular) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-eyebrow)',
  color: 'var(--bujo-text-meta)',
  paddingBottom: 'var(--bujo-space-2)',
};

const nav = {
  minHeight: 'var(--bujo-row-min)',
  font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)',
  color: 'var(--bujo-text-meta)',
  cursor: 'pointer',
};
